/**
 * Player profiles: what any shopper may see of another (their display name, when they joined,
 * their counts), opened from a battle seat on every surface. Boxed opens a quick-view modal from a
 * seat, a chat name or a leaderboard row; here one dialog is mounted once (`ProfileProvider`) and
 * every seat opens it through `useProfile().showProfile(endUserId)`.
 */
import { LootApiError } from "@loot-os/client";
import { createContext, useContext } from "react";

export interface ProfileContextValue {
  /** Open the profile dialog for the end user a battle seat names (`seat.endUser.id`). */
  showProfile: (endUserId: string) => void;
}

export const ProfileContext = createContext<ProfileContextValue | null>(null);

/** Opens the player profile dialog; throws outside <ProfileProvider>. */
export function useProfile(): ProfileContextValue {
  const value = useContext(ProfileContext);
  if (!value) throw new Error("useProfile must be used inside <ProfileProvider>");
  return value;
}

/** What to tell the shopper when a profile could not be loaded. */
export function profileErrorText(err: unknown): string {
  if (err instanceof LootApiError && err.code === "not_found") {
    return "This player could not be found.";
  }
  return err instanceof Error && err.message ? err.message : "Could not load the profile.";
}
